import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { SERVICES, getService } from "@/data/services";

export function CaseStudiesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/case-studies").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Header />
      <main className="pt-36 sm:pt-40 pb-20 min-h-screen bg-[#FAFAFA]" data-testid="case-studies-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#06B6D4]">Études de cas</div>
            <h1 className="display mt-3 text-4xl sm:text-5xl text-[#0A1128] font-extrabold leading-[1.05]">
              Quelques projets que nous avons menés.
            </h1>
            <p className="mt-4 text-[#475569] text-lg">
              De l'idée à la franchise : retrouvez ici une sélection de réalisations BESSON SOLUTIONS.
            </p>
          </div>

          {loading && <div className="mt-10 text-sm text-[#64748B]">Chargement…</div>}

          {!loading && items.length === 0 && (
            <div className="mt-12 bg-white rounded-3xl border border-dashed border-[#06B6D4]/40 p-10 text-center" data-testid="case-empty">
              <Sparkles className="mx-auto text-[#06B6D4]" size={40} />
              <h2 className="display text-xl font-extrabold text-[#0A1128] mt-3">De nouvelles études arrivent.</h2>
              <p className="text-sm text-[#64748B] mt-2">L'équipe prépare la publication de ses dernières réalisations. Revenez bientôt — ou découvrez nos services en attendant.</p>
              <Link to="/#parcours" className="inline-flex items-center gap-1 text-sm font-bold text-[#06B6D4] hover:underline mt-4">
                Voir nos 7 services <ArrowRight size={14} />
              </Link>
            </div>
          )}

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((c) => (
              <Link key={c.id} to={`/etudes-de-cas/${c.slug}`} data-testid={`case-card-${c.slug}`} className="group block bg-white rounded-3xl overflow-hidden border border-black/8 hover:shadow-2xl transition-all">
                <div className="aspect-video bg-gradient-to-br from-[#0A1128] to-[#14213d] relative overflow-hidden">
                  {c.cover_image ? (
                    <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-white/40 text-6xl display font-black">{(c.client_name || c.title || "?").slice(0, 1).toUpperCase()}</div>
                  )}
                  <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    {(c.services_used || []).slice(0, 2).map((sid) => {
                      const s = getService(sid);
                      return s ? <span key={sid} className="text-[10px] font-bold tracking-wider uppercase bg-white/90 backdrop-blur text-[#0A1128] px-2 py-0.5 rounded-full">{s.short}</span> : null;
                    })}
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-xs text-[#06B6D4] font-bold uppercase tracking-widest">{c.sector || c.client_name || "Projet client"}</div>
                  <h3 className="display font-extrabold text-lg text-[#0A1128] mt-1 group-hover:text-[#06B6D4] transition-colors">{c.title}</h3>
                  <p className="text-sm text-[#475569] mt-2 line-clamp-3">{c.summary}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#06B6D4]">Lire l'étude <ArrowRight size={14} /></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

export function CaseStudyDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/case-studies/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <><Header /><main className="pt-36 sm:pt-40 pb-20 text-center min-h-screen bg-[#FAFAFA]">Chargement…</main><Footer /></>;
  if (notFound) return (
    <>
      <Header />
      <main className="pt-36 sm:pt-40 pb-20 text-center min-h-screen bg-[#FAFAFA]">
        <h1 className="display text-3xl font-extrabold text-[#0A1128]">Étude de cas introuvable</h1>
        <Link to="/etudes-de-cas" className="mt-4 inline-flex items-center text-[#06B6D4] font-bold hover:underline"><ArrowLeft size={14} className="mr-1" />Retour aux études de cas</Link>
      </main>
      <Footer />
    </>
  );

  return (
    <>
      <Header />
      <main className="pt-36 sm:pt-40 pb-20 min-h-screen bg-[#FAFAFA]" data-testid={`case-study-${slug}`}>
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/etudes-de-cas" className="text-xs text-[#06B6D4] hover:underline inline-flex items-center gap-1"><ArrowLeft size={12} /> Toutes les études</Link>

          {data.cover_image && (
            <img src={data.cover_image} alt={data.title} className="mt-6 w-full aspect-video object-cover rounded-3xl shadow-xl" />
          )}

          <div className="mt-6">
            <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#06B6D4]">{data.client_name || data.sector || "Étude de cas"}</div>
            <h1 className="display mt-2 text-4xl sm:text-5xl font-extrabold text-[#0A1128] leading-tight">{data.title}</h1>
            <p className="mt-4 text-lg text-[#475569]">{data.summary}</p>

            {data.services_used?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {data.services_used.map((sid) => {
                  const s = getService(sid);
                  return s ? <span key={sid} className="text-[11px] font-bold tracking-wider uppercase bg-[#06B6D4]/10 text-[#0A1128] border border-[#06B6D4]/30 px-3 py-1 rounded-full">{s.short}</span> : null;
                })}
              </div>
            )}
          </div>

          {data.metrics?.length > 0 && (
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {data.metrics.map((m, i) => (
                <div key={i} className="bg-white rounded-2xl p-4 border border-black/8 text-center">
                  <div className="display text-2xl font-extrabold text-[#06B6D4]">{m.value}</div>
                  <div className="text-[10px] text-[#64748B] mt-1 uppercase tracking-widest">{m.label}</div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-10 space-y-8">
            {data.challenge && <Section title="Le défi">{data.challenge}</Section>}
            {data.solution && <Section title="Notre approche">{data.solution}</Section>}
            {data.results && <Section title="Les résultats">{data.results}</Section>}
          </div>

          {data.gallery?.length > 0 && (
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {data.gallery.map((src, i) => <img key={i} src={src} alt={`${data.title} ${i + 1}`} className="rounded-2xl shadow-md" />)}
            </div>
          )}

          <div className="mt-12 bg-[#0A1128] text-white rounded-3xl p-8 text-center">
            <h2 className="display text-2xl font-extrabold">Un projet similaire en tête ?</h2>
            <p className="text-white/70 mt-2">Parlons-en en 15 minutes — gratuit, sans engagement.</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Link to="/rendez-vous" className="inline-flex items-center gap-1 px-5 py-3 rounded-full bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] font-bold text-sm">Prendre RDV découverte <ArrowRight size={14} /></Link>
              <Link to="/portail" className="inline-flex items-center gap-1 px-5 py-3 rounded-full border border-white/20 hover:bg-white/10 font-bold text-sm">Mon espace entrepreneur</Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#06B6D4]">{title}</div>
      <div className="display text-2xl sm:text-3xl font-extrabold text-[#0A1128] mt-1 leading-tight">{title}</div>
      <div className="mt-3 text-[#475569] leading-relaxed whitespace-pre-line text-[15px]">{children}</div>
    </section>
  );
}
